import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllPermissions } from '../store/slices/userSlice';

const PermissionContext = createContext();

export const PermissionProvider = ({ children }) => {
  const dispatch = useDispatch();
  const cachedCatalog = useSelector((state) => state.users?.allPermissions);
  const authUser = useSelector((state) => state.auth?.user);
  const impersonating = useSelector((state) => state.auth?.impersonating === true);
  const sessionKey = `${authUser?.id ?? authUser?.uuid ?? ''}:${impersonating ? '1' : '0'}`;
  const sessionKeyRef = useRef(sessionKey);

  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  const mapPermissions = (response) => {
    const permissionsData = Array.isArray(response)
      ? response
      : (response?.data ?? response ?? []);
    return (Array.isArray(permissionsData) ? permissionsData : []).map((perm) => ({
      id: perm.id,
      name: perm.name,
      key: perm.name?.toLowerCase().replace(/\s+/g, '-') || perm.id,
    }));
  };

  const fetchPermissions = useCallback(async ({ force = false } = {}) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      setLoading(true);
      setError(null);
      const response = await dispatch(getAllPermissions({ force })).unwrap();
      setAllPermissions(mapPermissions(response));
    } catch (err) {
      if (err?.response?.status === 403) {
        setAllPermissions([]);
      } else {
        console.error('Failed to load permissions:', err);
        setError(err);
        setAllPermissions([]);
      }
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  }, [dispatch]);

  // Impersonate / leave / user switch — drop catalog so it refetches for the new session.
  useEffect(() => {
    if (sessionKeyRef.current === sessionKey) return;
    sessionKeyRef.current = sessionKey;
    setHasFetched(false);
    setAllPermissions([]);
    setError(null);
  }, [sessionKey]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || hasFetched) return;

    // Prefer Redux catalog if already loaded (avoids remount refetch)
    if (Array.isArray(cachedCatalog) && cachedCatalog.length > 0) {
      setAllPermissions(mapPermissions(cachedCatalog));
      setHasFetched(true);
      return;
    }

    fetchPermissions({ force: true });
  }, [hasFetched, cachedCatalog, fetchPermissions]);

  const refetch = () => {
    fetchPermissions({ force: true });
  };

  return (
    <PermissionContext.Provider
      value={{
        allPermissions,
        loading,
        error,
        refetch,
        fetchPermissions,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionProvider');
  }
  return context;
};
